
import emitter from 'tiny-emitter/instance';
import { ControlElement } from '@jsonforms/core';
import { rendererProps, RendererProps, useJsonFormsEnumControl } from '@jsonforms/vue';
import { defineComponent } from 'vue';

const controlRenderer = defineComponent({
  name: 'destination-ip-control-renderer',
  props: {
    ...rendererProps<ControlElement>(),
    itemLabel: {
      type: String,
    },
  },
  setup(props: RendererProps<ControlElement>) {
    return useJsonFormsEnumControl(props);
  },
  data() {
    return {
      stringOptions: [],
      inputText: '',
      isTouched: false,
      localError: '',
      ipPattern:
        '^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.' +
        '(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.' +
        '(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.' +
        '(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(/([0-9]|[1-2][0-9]|3[0-2]))?$',
    };
  },
  computed: {
    hasError(): boolean {
      return !!(this.control.errors || this.localError);
    },
    errorMessage(): string {
      if (this.localError) {
        return this.localError;
      }
      return this.control.errors || '';
    },
  },
  created() {
    emitter.on('setSourceIps', (ips: [string]) => {
      if (this.control?.uischema?.options?.key === 'source') {
        this.isTouched = true;
        this.onChange(ips);
      }
      if (!ips.length) {
        this.stringOptions = [];
      }
    });
    emitter.on('setDestinationIps', (ips: [string]) => {
      if (this.control?.uischema?.options?.key === 'destination') {
        this.isTouched = true;
        this.onChange(ips);
      }
    });
    emitter.on('setFocusOnSource', () => {
      if (this.control?.uischema?.options?.key === 'source') {
        this.focusInput();
      }
    });
    emitter.on('setFocusOnDestination', () => {
      if (this.control?.uischema?.options?.key === 'destination') {
        this.focusInput();
      }
    });
  },
  methods: {
    focusInput() {
      const applyFocus = () => {
        const selectComponent = this.$refs.selectRef as any;
        if (!selectComponent) {
          return;
        }

        selectComponent.hidePopup();
        selectComponent.focus();

        const nativeInput = selectComponent.$el?.querySelector('input');
        if (nativeInput) {
          nativeInput.focus();
        }
      };

      this.$nextTick(() => {
        applyFocus();
        setTimeout(applyFocus, 0);
        setTimeout(applyFocus, 50);
      });
    },

    normalizeIps(values: string[] = []): string[] {
      return values
        .flatMap((value) => value.split(','))
        .map((ip) => ip.trim())
        .filter(Boolean);
    },

    onBlur() {
      this.isTouched = true;
      this.commitInput();
    },
    onEnterPress() {
      const selectComponent = this.$refs.selectRef as any;
      if (selectComponent) {
        selectComponent.hidePopup();
      }
      this.commitInput();
    },
    onFocus() {
      // Optionally mark as touched on focus
    },
    createValue(val: any, done: any) {
      const trimmedVal = val.trim();
      if (trimmedVal.length > 0) {
        this.isTouched = true;
        const normalizedValues = this.normalizeIps([trimmedVal]);

        if (normalizedValues.length > 1) {
          const currentValue: string[] = this.control.data || [];
          const updated = [...currentValue, ...normalizedValues.filter((ip) => !currentValue.includes(ip))];
          this.validateIps(updated);
          this.handleChange(this.control.path, updated.length ? updated : undefined);
          done();
        } else {
          done(normalizedValues[0]);
        }
        this.focusInput();
      }
    },
    changeInputText(value: string) {
      this.inputText = value;
    },
    validateIps(value: string[] = []): void {
      this.localError = '';

      if (!value?.length) {
        this.localError = 'Field is required';
        return;
      }

      const ipRegex = new RegExp(this.ipPattern);
      const invalid = value?.find((ip) => !ipRegex.test(ip.trim()));
      if (invalid) {
        this.localError = `Invalid IP: ${invalid}`;
      }
    },

    onChange(value: string[] | null) {
      this.isTouched = true;

      const cleanedValues = this.normalizeIps(value ?? []);

      this.validateIps(cleanedValues);

      this.handleChange(this.control.path, cleanedValues.length ? cleanedValues : undefined);

      cleanedValues.forEach((val) => {
        if (!this.stringOptions.includes(val as never)) {
          this.stringOptions.push(val as never);
        }
      });
    },

    commitInput() {
      const currentValue: string[] = this.control.data || [];
      const trimmedInput = this.inputText.trim();

      if (trimmedInput) {
        const splitInput = this.normalizeIps([trimmedInput]);
        const updated = [...currentValue, ...splitInput.filter((ip) => !currentValue.includes(ip))];

        this.inputText = '';

        if (!updated.length || updated.length === currentValue.length) {
          this.focusInput();
          return;
        }

        this.validateIps(updated);
        this.handleChange(this.control.path, updated);
        this.focusInput();
      }
    },
  },
});

export default controlRenderer;
